import { z } from 'zod';
import { AudioMode, CaptionStyle, SlotAudio, TextMode, VideoFormat } from './media.js';
import { AUDIO_RAMP_MS } from './constants.js';
import { SearchFilters } from './search.js';
/** Plan parsed from the brief bar prompt. */
export const ComposePlan = z.object({
    /** A short 2-4 word name for the edit ("Pink Scenes"), never the whole brief. */
    title: z.string().default(''),
    slotCount: z.number().int().min(1).max(12),
    perSlotMs: z.number().int().min(500),
    slots: z.array(z.object({ filters: SearchFilters, targetMs: z.number().int().min(500) })),
    orderPattern: z.string(),
    audioMode: AudioMode,
    bedRequest: z.string().nullable(),
    voiceRequest: z.string().nullable(),
});
/** JSON schema handed to Vertex for plan parsing; kept beside the zod schema so they cannot drift. */
export const COMPOSE_PLAN_JSON_SCHEMA = {
    type: 'object',
    required: ['title', 'slotCount', 'perSlotMs', 'slots', 'orderPattern', 'audioMode', 'bedRequest', 'voiceRequest'],
    properties: {
        title: { type: 'string' },
        slotCount: { type: 'integer' },
        perSlotMs: { type: 'integer' },
        slots: {
            type: 'array',
            items: {
                type: 'object',
                required: ['filters', 'targetMs'],
                properties: {
                    filters: {
                        type: 'object',
                        required: ['facets', 'roles', 'creators', 'unusedDays', 'spokenText', 'residual'],
                        properties: {
                            facets: { type: 'array', items: { type: 'object', required: ['key', 'value'], properties: { key: { type: 'string' }, value: { type: 'string' } } } },
                            roles: { type: 'array', items: { type: 'string' } },
                            creators: { type: 'array', items: { type: 'string' } },
                            unusedDays: { type: 'integer', nullable: true },
                            spokenText: { type: 'string', nullable: true },
                            residual: { type: 'string' },
                        },
                    },
                    targetMs: { type: 'integer' },
                },
            },
        },
        orderPattern: { type: 'string' },
        audioMode: { type: 'string', enum: AudioMode.options },
        bedRequest: { type: 'string', nullable: true },
        voiceRequest: { type: 'string', nullable: true },
    },
};
/** Composer edit-pace presets: how long each slot runs in an auto-assembled cut. Contiguous ranges. */
export const EDIT_PACES = [
    { key: 'fast', label: 'Fast edits', minMs: 400, maxMs: 700, hint: '0.4–0.7 s per clip · good for TikTok and high energy' },
    { key: 'medium', label: 'Medium', minMs: 700, maxMs: 2000, hint: '0.7–2 s per clip · good for Instagram, Facebook and general use' },
    { key: 'long', label: 'Long clips', minMs: 2000, maxMs: 3500, hint: '2–3.5 s per clip · luxury and slow-paced content; an unhurried, unstressful edit' },
    { key: 'cinematic', label: 'Cinematic', minMs: 3500, maxMs: 6000, hint: '3.5–6 s per clip · big sweeps and slow scenes' },
];
/**
 * Where a remix holds each row inside the brand's pace, from its shortest (0) to its longest (1): a row
 * showing one of the brand's primary moments holds longest, a secondary one longer than the rest. The
 * rest sit mid-pace, since a floor like 0.2 s is a flicker, not a shot (Dax, 2026-09-28).
 */
export const REMIX_PACE_SHARE = { primary: 1, secondary: 0.75, other: 0.5 };
/** How a remix ranks a moment by the brand's priorities: primary first. */
export const REMIX_RANK = { primary: 0, secondary: 1, other: 2 };
/** How far a clip's sound effect may be pushed from the clip's first frame, either way. */
export const SFX_NUDGE_MAX_MS = 30_000;
/**
 * Trims (Dax, 2026-10-03): every sound on the desk plays a stretch of its file, from `fromMs` to `toMs`
 * inside it (null: to its end), dragged on handles in the audio panel. Trimming never moves the sound in
 * time: the file keeps its place on the cut and only the heard stretch changes. A trimmed edge gets the
 * site-wide ramp so it never clicks; an end that "Trim clip" put at a clip's last frame fades over
 * CLIP_TRIM_FADE_MS instead, finishing at the cut, so the next clip's sound starts clean.
 */
export const CLIP_TRIM_FADE_MS = 300;
/** The shortest stretch a handle may leave. */
export const TRIM_MIN_MS = 50;
/**
 * What of a trimmed sound plays on a cut `cutMs` long: where it starts on the cut, how far into the file,
 * how long, and its ramps. Anything before the cut's first frame (pushed earlier than 0.00) or after its
 * last is cut off, with the ramp there. Null when none of it falls inside.
 */
export function placeSound(t, cutMs) {
    const from = Math.max(0, Math.min(t.fromMs, t.fileMs));
    const to = Math.max(from, Math.min(t.toMs ?? t.fileMs, t.fileMs));
    const begins = Math.max(t.atMs + from, 0);
    const ends = Math.min(t.atMs + to, cutMs);
    if (ends <= begins)
        return null;
    const heard = ends - begins;
    const ramp = Math.min(AUDIO_RAMP_MS, heard / 2);
    const endsAtTrim = ends === t.atMs + to;
    return {
        atMs: begins,
        fromMs: begins - t.atMs,
        durationMs: heard,
        fadeInMs: begins > t.atMs ? ramp : 0,
        fadeOutMs: endsAtTrim && t.endFadeMs !== null ? Math.min(t.endFadeMs, heard / 2) : ends < t.atMs + t.fileMs ? ramp : 0,
    };
}
/**
 * Trim clip, held as a toggle (Dax, 2026-10-04): a sound's trim kept inside its own clip (the file starting
 * at `atMs`, the clip from `clipStartMs` to `clipEndMs`, all on the cut). A handle already dragged inside
 * the clip stays where it is; anything outside is cut back to the clip's edge, where an end that cut the
 * sample short fades over CLIP_TRIM_FADE_MS into the cut. A trim left with nothing inside the clip opens to
 * the clip's whole window. Null when none of the file falls inside its clip.
 */
export function clampToClip(trim, clipStartMs, clipEndMs, atMs, fileMs) {
    const lo = Math.max(0, Math.round(clipStartMs - atMs));
    const hi = Math.min(fileMs, Math.round(clipEndMs - atMs));
    if (hi - lo < TRIM_MIN_MS)
        return null;
    let from = Math.max(trim.fromMs, lo);
    let to = Math.min(trim.toMs ?? fileMs, hi);
    if (to - from < TRIM_MIN_MS) {
        from = lo;
        to = hi;
    }
    const cutAtClipEnd = to === hi && atMs + fileMs > clipEndMs;
    return { fromMs: from, toMs: to >= fileMs ? null : to, endFadeMs: cutAtClipEnd ? CLIP_TRIM_FADE_MS : to < hi ? trim.endFadeMs : null };
}
/** Whether a trim reaches outside its clip: dragging a handle past the clip's edge switches Trim clip off. */
export function trimLeavesClip(trim, clipStartMs, clipEndMs, atMs, fileMs) {
    return trim.fromMs < Math.round(clipStartMs - atMs) || (trim.toMs ?? fileMs) > Math.round(clipEndMs - atMs);
}
/**
 * Levelling (Dax, 2026-10-03): each sound is measured over what actually plays, by its loudest stretch of
 * LEVEL_WINDOW_MS (the momentary loudness window) and its peak, and given the gain that brings that
 * stretch to its channel's target without its peak passing LEVEL_PEAK_CEILING_DB. Foreground channels sit
 * at the library's own level (its proxies are levelled to -14 LUFS); beds sit 12 dB under them, where the
 * desk's own -12 dB defaults put them. The channel knobs and the master stay faders on top.
 */
export const LEVEL_WINDOW_MS = 400;
export const LEVEL_HOP_MS = 100;
/**
 * A song's silent opening is skipped as it arrives on the music row (Dax, 2026-10-04): the segment starts
 * MUSIC_LEAD_PREROLL_MS before the first sample above MUSIC_SIGNAL_FLOOR_DB, when the silence runs at
 * least MUSIC_LEAD_SILENCE_MIN_MS. Shorter openings are part of the song and stay.
 */
export const MUSIC_SIGNAL_FLOOR_DB = -45;
export const MUSIC_LEAD_SILENCE_MIN_MS = 50;
export const MUSIC_LEAD_PREROLL_MS = 3;
/**
 * A clip's sound effect skips its silent opening the same way (Dax, 2026-10-04), starting at the clip's
 * first frame with this much lead-in before its sound, so the attack is heard whole.
 */
export const SOUND_LEAD_PREROLL_MS = 20;
/**
 * The longest words a clip's sound is found by and kept with. A clip's own words (what it shows, what was heard)
 * can run past it; written longer, the whole composition failed to load (Dax, 2026-10-06), so they are cut to fit.
 */
export const SOUND_QUERY_MAX = 300;
export const LEVEL_PEAK_CEILING_DB = -1;
export const LEVEL_TARGET_DB = { clips: -14, asmr: -14, voice: -14, music: -26, soundtrack: -26 };
/** The gain, in whole decibels inside the knob's range, that levels a measured sound; null for silence. */
export function levelGainDb(measured, targetDb, range) {
    if (!Number.isFinite(measured.loudestDb) || !Number.isFinite(measured.peakDb))
        return null;
    const gain = Math.min(targetDb - measured.loudestDb, LEVEL_PEAK_CEILING_DB - measured.peakDb);
    return Math.max(range.min, Math.min(range.max, Math.round(gain)));
}
/**
 * A slot's sound effect, the ASMR channel's sound for this clip: a library effect layered over the
 * clip's own sound. The whole sample plays (Dax, 2026-09-29): `offsetMs` is where it starts against the
 * clip's first frame, so a slow attack can be pushed early into the clips before and a tail runs on under
 * the clips after. Video never overlaps by a frame; its sound may, to keep the attack and the tail.
 */
export const SlotAudio2 = z.object({
    bedId: z.string().uuid(),
    gainDb: z.number().min(-60).max(12).default(0),
    offsetMs: z.number().int().min(-SFX_NUDGE_MAX_MS).max(SFX_NUDGE_MAX_MS).default(0),
    /** Turned all the way down on its knob: kept, and silent. */
    muted: z.boolean().default(false),
    /** The words it was found by, so refresh can look again for the same thing (Dax, 2026-09-20). */
    query: z.string().max(SOUND_QUERY_MAX).nullable().default(null),
    /** How far down the candidate list this one sat; refresh advances it, so "next" means next. */
    cursor: z.number().int().min(0).default(0),
    /** exact: the words matched; close: the nearest by meaning stood in, as video slots report it. */
    match: z.enum(['exact', 'close']).nullable().default(null),
    /** The stretch of the sample that plays (`placeSound`): from `trimFromMs` to `trimToMs` (null: its end). */
    trimFromMs: z.number().int().min(0).default(0),
    trimToMs: z.number().int().min(0).nullable().default(null),
    /** How its end fades: null is the site-wide ramp (or none at the sample's own end); Trim clip sets a longer fade. */
    endFadeMs: z.number().int().min(0).nullable().default(null),
});
/**
 * On-screen text: fonts that exist on the render host (fontconfig names), with a browser stack that
 * approximates each for the live preview canvas.
 */
export const TEXT_FONTS = [
    { id: 'liberation-sans', label: 'Liberation Sans', fc: 'Liberation Sans', css: 'Arial, Helvetica, sans-serif' },
    { id: 'dejavu-sans', label: 'DejaVu Sans', fc: 'DejaVu Sans', css: 'Verdana, sans-serif' },
    { id: 'liberation-serif', label: 'Liberation Serif', fc: 'Liberation Serif', css: '"Times New Roman", serif' },
    { id: 'dejavu-serif', label: 'DejaVu Serif', fc: 'DejaVu Serif', css: 'Georgia, serif' },
    { id: 'charter', label: 'Bitstream Charter', fc: 'Bitstream Charter', css: 'Charter, Georgia, serif' },
    { id: 'liberation-mono', label: 'Liberation Mono', fc: 'Liberation Mono', css: '"Courier New", monospace' },
    { id: 'dejavu-mono', label: 'DejaVu Sans Mono', fc: 'DejaVu Sans Mono', css: 'Menlo, Consolas, monospace' },
    // Google fonts, installed on the render host by infra/vps-fonts.sh and loaded in the web app's
    // index.html so the sketch and the burn agree.
    { id: 'montserrat', label: 'Montserrat', fc: 'Montserrat', css: 'Montserrat, Arial, sans-serif' },
    { id: 'poppins', label: 'Poppins', fc: 'Poppins', css: 'Poppins, Arial, sans-serif' },
    { id: 'oswald', label: 'Oswald', fc: 'Oswald', css: 'Oswald, "Arial Narrow", sans-serif' },
    { id: 'bebas-neue', label: 'Bebas Neue', fc: 'Bebas Neue', css: '"Bebas Neue", "Arial Narrow", sans-serif' },
    { id: 'pacifico', label: 'Pacifico', fc: 'Pacifico', css: 'Pacifico, cursive' },
    { id: 'roboto', label: 'Roboto', fc: 'Roboto', css: 'Roboto, Arial, sans-serif' },
];
const HEX_COLOUR = /^#[0-9a-fA-F]{6}$/;
/** One styled row of on-screen text; every row is independent, per the display builder. */
export const TextLine = z.object({
    text: z.string().max(200),
    font: z.string().default('liberation-sans'),
    sizePct: z.number().min(1).max(25).default(5),
    colour: z.string().regex(HEX_COLOUR).default('#ffffff'),
    bold: z.boolean().default(false),
    italic: z.boolean().default(false),
    align: z.enum(['left', 'center', 'right']).default('center'),
    yPct: z.number().min(2).max(98).default(50),
    background: z.enum(['none', 'box', 'band']).default('none'),
    backgroundColour: z.string().regex(HEX_COLOUR).default('#000000'),
    backgroundOpacity: z.number().min(0).max(1).default(0.5),
    outline: z.enum(['none', 'thin', 'thick']).default('none'),
    /** Free outline width 0..4 from the slider; null falls back to the legacy none/thin/thick. */
    outlineW: z.number().min(0).max(4).nullable().default(null),
    outlineColour: z.string().regex(HEX_COLOUR).default('#000000'),
    shadow: z.boolean().default(false),
    /** When shown, in composite milliseconds; null spans the whole cut. */
    fromMs: z.number().int().min(0).nullable().default(null),
    toMs: z.number().int().min(0).nullable().default(null),
});
/** The outline width ladder: fine steps under 1, then the familiar log spread up to 4. */
export const OUTLINE_STOPS = [0, 0.1, 0.2, 0.3, 0.5, 0.7, 1, 1.5, 2, 3, 4];
/** The caption styling overrides on top of the chosen preset; null fields keep the preset's look. */
export const CaptionOverride = z.object({
    font: z.string().nullable().default(null),
    sizePct: z.number().min(1).max(15).nullable().default(null),
    colour: z.string().regex(HEX_COLOUR).nullable().default(null),
    outlineColour: z.string().regex(HEX_COLOUR).nullable().default(null),
    outlineW: z.number().min(0).max(4).nullable().default(null),
    shadow: z.boolean().nullable().default(null),
    /** Vertical position, % from the top, like the title builder's Position. */
    yPct: z.number().min(2).max(98).nullable().default(null),
});
/**
 * Whole-mix audio processing, everything off by default. Normalisation especially: loudnorm levels
 * the final loudness, which CANCELS the user's overall gain moves (a -24 dB timeline was pulled
 * straight back up); it is an opt-in final polish, never the resting state.
 */
export const AudioFx = z.object({
    normalise: z.boolean().default(false),
    compression: z.enum(['off', 'low', 'mid', 'high']).default('off'),
    bassDb: z.number().min(-12).max(6).default(0),
    trebleDb: z.number().min(-12).max(6).default(0),
});
/**
 * The compression presets, one table for the render's acompressor and the browser preview's
 * DynamicsCompressor, so what is heard while composing is what Export does.
 */
export const COMPRESSION_PRESETS = {
    low: { thresholdDb: -18, ratio: 2, attackMs: 20, releaseMs: 250 },
    mid: { thresholdDb: -20, ratio: 3, attackMs: 15, releaseMs: 200 },
    high: { thresholdDb: -24, ratio: 4, attackMs: 10, releaseMs: 150 },
};
/** FFmpeg's own bass and treble shelf frequencies, which the preview's shelving filters copy. */
export const BASS_SHELF_HZ = 100;
export const TREBLE_SHELF_HZ = 3000;
/** How far a ducked clip's own sound drops under a voiceover. */
export const CLIP_DUCK_DB = -12;
/** A pushed sound effect always keeps at least this much inside its own clip, so it can be grabbed again. */
export const SFX_NUDGE_MIN_OVERLAP_MS = 100;
export const DraftSlot = z.object({
    snippetId: z.string().uuid(),
    inMs: z.number().int().min(0),
    outMs: z.number().int().min(0),
    gainDb: z.number().min(-60).max(12).default(0),
    audio: SlotAudio.default('keep'),
    audio2: SlotAudio2.nullable().default(null),
    prompt: z.string().nullable().default(null),
    /** Per-clip burned text: shown for the slot's whole duration, however the trim handles move. */
    overlay: z.array(TextLine).max(6).default([]),
    /**
     * exact: it shows what was asked; close: nothing does, and the nearest by meaning stood in; taken: the clip
     * that does show it is already in this edit, and this is the nearest other (2026-10-04).
     */
    match: z.enum(['exact', 'close', 'taken']).nullable().default(null),
});
/**
 * The voiceover: a library voiceover (`bedId`, a recording or a clean spoken line judged out of a clip),
 * or a line picked straight off a clip in the Analyser (`assetId` and its span). Exactly one of the two.
 */
/** The longest a segment's search words are kept. */
export const SEGMENT_QUERY_MAX = 300;
/**
 * One piece of a full-length channel (Dax, 2026-10-04): music, voiceover and soundtrack each hold an
 * ordered list of these, laid on the cut where they were placed, so a channel can run one song into the
 * next or several voice passages one after another. The file is a library sound (`bedId`) or a clip's own
 * audio (`assetId`, its whole timeline, of which the trim picks a line or a passage). `atMs` is where the
 * file's start sits on the cut; `fromMs` to `toMs` (null: its end) is the stretch that plays, in place.
 * `gainDb` is the segment's own level, under the channel's fader; `query` and `cursor` are what Replace
 * searched by and how far down the candidates it has gone.
 */
export const LayerSegment = z.object({
    bedId: z.string().uuid().nullable().default(null),
    assetId: z.string().uuid().nullable().default(null),
    fromMs: z.number().int().min(0).default(0),
    toMs: z.number().int().min(0).nullable().default(null),
    atMs: z.number().int().default(0),
    gainDb: z.number().min(-60).max(12).default(0),
    query: z.string().max(SEGMENT_QUERY_MAX).nullable().default(null),
    cursor: z.number().int().min(0).default(0),
}).refine((v) => (v.bedId === null) !== (v.assetId === null), { message: 'A segment is a library sound or a clip\'s own audio, never both' });
export const LAYER_CHANNELS = ['music', 'voice', 'soundtrack'];
export const DraftLayers = z.object({
    music: z.array(LayerSegment).default([]),
    voice: z.array(LayerSegment).default([]),
    soundtrack: z.array(LayerSegment).default([]),
});
/** The stretch of a segment's file that plays, inside the file. */
export function segmentStretch(seg, fileMs) {
    const fromMs = Math.max(0, Math.min(seg.fromMs, fileMs));
    return { fromMs, toMs: Math.max(fromMs, Math.min(seg.toMs ?? fileMs, fileMs)) };
}
/** Where a segment added to a channel starts on the cut: where the last heard stretch ends (0.00 when empty). */
export function nextSegmentAtMs(placed) {
    return placed.reduce((end, p) => Math.max(end, p.atMs + segmentStretch(p, p.fileMs).toMs), 0);
}
/**
 * Voiceover takes, scored (Dax, 2026-10-04): how well a take's words match what the composition is about
 * (`cosine`, from the embeddings) and how near its length is to the length wanted, generously, since a
 * take can be dragged and trimmed. A take is confident when it means the right thing and its length is
 * within VOICE_FIT_TOLERANCE_MS; the composition's own clips are tried before the library.
 */
export const VOICE_FIT_TOLERANCE_MS = 8000;
export function voiceTakeScore(cosine, durationMs, targetMs) {
    const fit = 1 / (1 + Math.abs(durationMs - targetMs) / VOICE_FIT_TOLERANCE_MS);
    return cosine * (0.5 + 0.5 * fit);
}
export function voiceTakeConfident(cosine, durationMs, targetMs, minCosine) {
    return cosine >= minCosine && Math.abs(durationMs - targetMs) <= VOICE_FIT_TOLERANCE_MS;
}
/** The audio desk's channels, top to bottom (Dax, 2026-09-29). */
export const AUDIO_CHANNELS = ['clips', 'asmr', 'music', 'voice', 'soundtrack'];
/** Each channel's speaker: a muted track keeps its level and its choices, it just is not heard. */
export const DraftMutes = z.object({
    clips: z.boolean().default(false),
    asmr: z.boolean().default(false),
    music: z.boolean().default(false),
    voice: z.boolean().default(false),
    soundtrack: z.boolean().default(false),
});
/**
 * Each channel's solo (Dax, 2026-09-30): while any channel is soloed, only the soloed ones are heard.
 * Solos add up (a second solo joins the first) and each comes off on its own. Saved with the draft, so
 * Export plays exactly what was heard.
 */
export const DraftSolos = DraftMutes;
/** Each channel's Level toggle (Dax, 2026-10-04): on, a sound or clip arriving on it is levelled automatically. */
export const DraftAutoLevel = z.object({
    clips: z.boolean().default(true),
    asmr: z.boolean().default(true),
    music: z.boolean().default(true),
    voice: z.boolean().default(true),
    soundtrack: z.boolean().default(true),
    /** The master: on, its gain is corrected after each pass whenever the mix sits too low or clips. */
    master: z.boolean().default(true),
});
/** Whether a channel is heard: not muted, and soloed whenever anything is. */
export function channelHeard(ch, mutes, solos) {
    const soloing = AUDIO_CHANNELS.some((c) => solos[c]);
    return !mutes[ch] && (!soloing || solos[ch]);
}
export const DraftText = z.object({
    mode: TextMode,
    style: CaptionStyle.nullable(),
    title: z.string().nullable(),
    lines: z.array(TextLine).max(12).default([]),
    /** The user-edited caption script; null burns the detected words verbatim. Timing is re-fitted per render. */
    script: z.string().max(4000).nullable().default(null),
    capStyle: CaptionOverride.default({}),
});
/** The renderer's view of the draft's text: styled lines, with the legacy plain title as one line. */
export function textLinesFromDraftText(text) {
    if (text.mode !== 'title')
        return [];
    if (text.lines.length)
        return text.lines;
    if (text.title)
        return [TextLine.parse({ text: text.title, yPct: 12, outline: 'thin', fromMs: 0, toMs: 3000 })];
    return [];
}
export const Draft = z.object({
    briefText: z.string(),
    plan: ComposePlan.nullable(),
    format: VideoFormat,
    targetMs: z.number().int(),
    slots: z.array(DraftSlot),
    /** Music, voiceover and soundtrack: each an ordered list of segments (2026-10-04). */
    layers: DraftLayers.default({}),
    /** The full-length channels' faders, on top of each segment's own level. */
    bedGainDb: z.number().default(-12),
    musicGainDb: z.number().default(-12),
    voiceGainDb: z.number().min(-60).max(12).default(0),
    /** The whole mix's level, after the master EQ (2026-10-04). */
    masterGainDb: z.number().min(-60).max(24).default(0),
    /** Timeline volume: added to every clip's own gain across the whole cut. */
    clipsGainDb: z.number().min(-60).max(12).default(0),
    /** The ASMR channel's level: added to every clip's own sound effect level. */
    asmrGainDb: z.number().min(-60).max(12).default(0),
    mutes: DraftMutes.default({}),
    solos: DraftSolos.default({}),
    /** Trim clip, as a toggle: while on, every clip's sound stays inside its own clip (`clampToClip`). */
    asmrTrim: z.boolean().default(false),
    autoLevel: DraftAutoLevel.default({}),
    audioFx: AudioFx.default({}),
    text: DraftText,
});
/** Measured channel levels for the mixer's meters, taken from the mix BEFORE loudness normalisation. */
export const ChannelLevel = z.object({ meanDb: z.number(), peakDb: z.number() });
export const RenderAudioStats = z.object({
    clips: ChannelLevel.nullable().default(null),
    asmr: ChannelLevel.nullable().default(null),
    soundtrack: ChannelLevel.nullable().default(null),
    music: ChannelLevel.nullable().default(null),
    voice: ChannelLevel.nullable().default(null),
    master: ChannelLevel.nullable().default(null),
});
export const CLIPPING_PEAK_DB = -0.1;
/**
 * The whole mix's level (Dax, 2026-10-31): its loudest stretch sits here, measured as a sound is by
 * `levelGainDb`, so stacking lanes brings the master down and taking them away brings it back. About the loudness
 * short-form video plays at; a mean-level target could not be met, since real mixes average near -25 dB.
 */
export const MASTER_TARGET_LOUDEST_DB = -10;
/** A mix this close to its level is left alone, so the master does not move for every small change. */
export const MASTER_LEVEL_BAND_DB = 1;
/** How long what plays must sit still before the master levels it, so a turn or a sound arriving is one move. */
export const MASTER_LEVEL_SETTLE_MS = 600;
/** Where the master's automatic correction keeps its peaks: under the clipping line with a little room. */
export const MASTER_PEAK_CEILING_DB = -1;
/** The master knob's range, wider than a channel's: a quiet mix may need a lot of lift. */
export const MASTER_GAIN_MIN_DB = -24;
export const MASTER_GAIN_MAX_DB = 24;
const masterInRange = (db) => Math.max(MASTER_GAIN_MIN_DB, Math.min(MASTER_GAIN_MAX_DB, db));
/**
 * The master gain a whole mix calls for, given the mix measured before the master and the gain it has now; null
 * when it is fine as it is or silent. Whole decibels, inside the knob's range, never past what the peaks allow.
 */
export function masterLevelDb(mix, gainDb) {
    if (!Number.isFinite(mix.loudestDb) || !Number.isFinite(mix.peakDb))
        return null;
    const headroom = MASTER_PEAK_CEILING_DB - mix.peakDb;
    const next = masterInRange(Math.min(Math.round(MASTER_TARGET_LOUDEST_DB - mix.loudestDb), Math.floor(headroom)));
    return gainDb <= headroom && Math.abs(next - gainDb) <= MASTER_LEVEL_BAND_DB ? null : next;
}
/**
 * What a reading of the mix as it was heard calls for, or null: only clipping, pulled back under the ceiling. A
 * pass hears only what was played, which proves a clip but says nothing about how loud the whole cut should be.
 */
export function masterCorrection(level, gainDb) {
    if (level.peakDb < CLIPPING_PEAK_DB)
        return null;
    const next = masterInRange(Math.floor(gainDb + MASTER_PEAK_CEILING_DB - level.peakDb));
    return next === gainDb ? null : next;
}
/** Fully resolved input to the renderer: every slot carries the file it cuts from. */
export const RenderSlot = z.object({
    file: z.string(),
    /** Where to start reading the file: `inMs` and `outMs` stay the file's own times. For a file read over the network. */
    seekMs: z.number().int().min(0).optional(),
    inMs: z.number().int(),
    outMs: z.number().int(),
    gainDb: z.number(),
    audio: SlotAudio,
    /** The slot's sound effect: the whole sample, starting `offsetMs` from the slot's first frame in the composite. */
    audio2: z.object({
        file: z.string(), gainDb: z.number(), offsetMs: z.number().int().default(0), durationMs: z.number().int().min(1),
        trimFromMs: z.number().int().min(0).default(0), trimToMs: z.number().int().min(0).nullable().default(null), endFadeMs: z.number().int().min(0).nullable().default(null),
    }).nullable().default(null),
    /** The next slot continues this one across a part join: no fade out, no fade in, no crossfade between them. */
    seamlessNext: z.boolean().default(false),
    /** Whether the file carries a sound track at all; a silent one (AI-generated footage, a screen recording) plays silence. */
    hasAudio: z.boolean(),
});
export const RenderSpec = z.object({
    kind: z.enum(['draft', 'final']),
    format: VideoFormat,
    slots: z.array(RenderSlot).min(1),
    /**
     * Every heard segment of the full-length channels: `file` holds `fromMs` to `toMs` (inside it) of what
     * plays, starting `atMs` on the cut; `gainDb` is the segment's level plus its channel's fader.
     */
    layers: z.array(z.object({ channel: z.enum(LAYER_CHANNELS), file: z.string(), gainDb: z.number(), fromMs: z.number().int().min(0), toMs: z.number().int().min(0), atMs: z.number().int() })).default([]),
    captions: z.object({ assFile: z.string() }).nullable(),
    textLines: z.array(TextLine).default([]),
    fx: AudioFx.default({}),
    /** The whole mix's level, after the EQ and before loudness normalising. */
    masterGainDb: z.number().optional(),
    output: z.string(),
});
/**
 * The next sound for a clip's effect channel when a person asks for another (Dax, 2026-10-01): another
 * of the same kind as the one playing (its sound category; the best match's when nothing plays yet),
 * in the order the clip's own words ranked them, wrapping at the end. A sound with no category steps
 * through every candidate. Null when the kind holds nothing else, so the caller can say the library is
 * short of that kind instead of offering a sound of another. A sound already on the cut waits until the kind
 * holds nothing else (Dax, 2026-10-31): asking for another should not bring back what is already playing.
 */
export function nextSoundOfKind(ranked, current, onCut = new Set()) {
    const kind = current ? current.category : ranked[0]?.category ?? null;
    const sameKind = kind === null ? [...ranked] : ranked.filter((r) => r.category === kind);
    if (current && !sameKind.some((r) => r.bedId === current.bedId))
        sameKind.unshift({ bedId: current.bedId, category: kind });
    if (!sameKind.some((r) => r.bedId !== current?.bedId))
        return null;
    // Next after the one playing; the first when nothing plays.
    const at = current ? sameKind.findIndex((r) => r.bedId === current.bedId) : -1;
    const walk = sameKind.map((_, step) => (at + step + 1) % sameKind.length).filter((i) => sameKind[i].bedId !== current?.bedId);
    const i = walk.find((n) => !onCut.has(sameKind[n].bedId)) ?? walk[0];
    return { pick: sameKind[i], cursor: i, of: sameKind.length };
}
/** Every library sound a cut already plays: each clip's effect and each full-length segment's file. */
export function soundsOnCut(draft) {
    const beds = [...draft.slots.map((s) => s.audio2?.bedId), ...Object.values(draft.layers).flat().map((g) => g.bedId)];
    return new Set(beds.filter((id) => Boolean(id)));
}
/** What a clip can be remixed for (Dax, 2026-10-01). Every figure is global; each brand's own pace scales it. */
export const RemixTarget = z.enum(['tiktok', 'instagram', 'youtube_shorts', 'youtube', 'open']);
/** The sound a Composer remix is made with (Dax, 2026-10-04): trending music, every clip's effect, or a voiceover. */
export const RemixAudio = z.enum(['music', 'asmr', 'voice']);
/** The platforms the Composer remixes for, in its toolbar's order. */
export const COMPOSE_REMIX_TARGETS = ['tiktok', 'instagram', 'youtube'];
// One set of lengths, shared by the Analyser's Remix and the Composer's (Dax, 2026-10-04).
export const REMIX_PRESETS = {
    tiktok: { label: 'TikTok edit', platform: 'TikTok', summary: '5 to 7 s, very fast cuts, trending music', minMs: 5000, maxMs: 7000, paceFactor: 0.6, basePace: true, wholeLines: false, music: 'always', format: '9:16', wideOnly: false, bestOnly: false, defaultAudio: 'music' },
    instagram: { label: 'Instagram', platform: 'Instagram', summary: '10 to 15 s, the best-looking shots, voice kept when whole', minMs: 10000, maxMs: 15000, paceFactor: 1, basePace: true, wholeLines: true, music: 'unvoiced', format: '9:16', wideOnly: false, bestOnly: false, defaultAudio: 'music' },
    youtube_shorts: { label: 'YouTube Shorts', platform: 'YouTube Shorts', summary: '15 to 30 s, voice kept when whole', minMs: 15000, maxMs: 30000, paceFactor: 1.3, basePace: true, wholeLines: true, music: 'unvoiced', format: '9:16', wideOnly: false, bestOnly: false, defaultAudio: 'voice' },
    youtube: { label: 'YouTube', platform: 'YouTube', summary: '30 to 60 s upright, voice kept when whole', minMs: 30000, maxMs: 60000, paceFactor: 1.3, basePace: true, wholeLines: true, music: 'unvoiced', format: '9:16', wideOnly: false, bestOnly: false, defaultAudio: 'voice' },
    open: { label: 'Open', platform: 'Open', summary: 'Every moment switched on, at the brand\'s pace', minMs: null, maxMs: null, paceFactor: 1, basePace: false, wholeLines: true, music: 'never', format: null, wideOnly: false, bestOnly: false, defaultAudio: 'music' },
};
/**
 * The sound a Composer remix offers first: the platform's own, except TikTok, which alternates between
 * music and ASMR (Dax, 2026-10-04), offering whichever of the two was not chosen last time.
 */
export function remixAudioDefault(target, lastTiktok) {
    if (target !== 'tiktok' || lastTiktok === null || lastTiktok === 'voice')
        return REMIX_PRESETS[target].defaultAudio;
    return lastTiktok === 'music' ? 'asmr' : 'music';
}
/**
 * A new order for a cut's clips (Dax, 2026-10-04): a seeded shuffle, so the same seed gives the same order,
 * that never comes back as the order it started from. One clip stays as it is.
 */
export function remixOrder(n, seed) {
    const order = Array.from({ length: n }, (_, i) => i);
    // mulberry32: small, fast and good enough to shuffle a handful of clips.
    let a = seed >>> 0;
    const rand = () => {
        a = (a + 0x6d2b79f5) >>> 0;
        let t = a;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
    for (let i = n - 1; i > 0; i--) {
        const j = Math.floor(rand() * (i + 1));
        [order[i], order[j]] = [order[j], order[i]];
    }
    return n > 1 && order.every((v, i) => v === i) ? [...order.slice(1), order[0]] : order;
}
/**
 * How long each clip of a remix runs so the cut lands inside `minMs` to `maxMs` (Dax, 2026-10-04); null
 * drops it. Too long: clips held past the platform's pace are trimmed towards it, then the weakest go
 * (highest rank, the later first) while the cut stays long enough, then every clip shortens alike, never
 * under `minCutMs`. Too short: each clip grows inside its snippet up to the pace, and `shortMs` is what is
 * still missing, for clips from the library.
 */
export function fitToTarget(slots, minMs, maxMs, paceMaxMs, minCutMs) {
    const lens = slots.map((s) => s.lenMs);
    const total = () => lens.reduce((acc, l) => acc + (l ?? 0), 0);
    // Every share is floored, so a cut never ends up a millisecond over what was asked.
    const shrink = (room, excess) => {
        const all = room.reduce((acc, r) => acc + r, 0);
        if (all <= 0)
            return;
        const take = Math.min(1, excess / all);
        room.forEach((r, i) => { if (lens[i] !== null)
            lens[i] = lens[i] - Math.ceil(r * take); });
    };
    if (total() > maxMs) {
        shrink(lens.map((l) => (l === null ? 0 : Math.max(0, l - Math.max(paceMaxMs, minCutMs)))), total() - maxMs);
        const weakest = slots.map((s, i) => ({ i, rank: s.rank })).sort((a, b) => b.rank - a.rank || b.i - a.i);
        for (const { i } of weakest) {
            if (total() <= maxMs || lens.filter((l) => l !== null).length <= 1)
                break;
            if (total() - lens[i] >= minMs)
                lens[i] = null;
        }
        if (total() > maxMs)
            shrink(lens.map((l) => (l === null ? 0 : Math.max(0, l - minCutMs))), total() - maxMs);
        return { lens, shortMs: 0 };
    }
    const need = minMs - total();
    if (need <= 0)
        return { lens, shortMs: 0 };
    const room = slots.map((s, i) => Math.max(0, Math.min(s.growMs, paceMaxMs - lens[i])));
    const all = room.reduce((acc, r) => acc + r, 0);
    const give = Math.min(1, all > 0 ? need / all : 0);
    room.forEach((r, i) => { lens[i] = lens[i] + Math.floor(r * give); });
    return { lens, shortMs: Math.max(0, minMs - total()) };
}
/** At most this many cuts come from one moment when a remix splits it to reach its length. */
export const REMIX_CUTS_PER_MOMENT = 3;
/** The picture skipped between two cuts from one moment, as a share of a cut: enough that each reads as a cut. */
export const REMIX_SPLIT_GAP_SHARE = 0.5;
/**
 * One moment as several cuts at the same pace (Dax, 2026-10-06): `n` windows of `lenMs`, a gap of
 * REMIX_SPLIT_GAP_SHARE of a cut between each, laid as one run centred on the moment's best point and kept
 * inside it. Null when the moment is too short to hold them.
 */
export function splitMoment(bounds, centreMs, lenMs, n) {
    if (n < 1 || lenMs <= 0)
        return null;
    const gap = Math.round(lenMs * REMIX_SPLIT_GAP_SHARE);
    const run = n * lenMs + (n - 1) * gap;
    if (run > bounds.outMs - bounds.inMs)
        return null;
    const start = Math.min(bounds.outMs - run, Math.max(bounds.inMs, Math.round(centreMs - run / 2)));
    return Array.from({ length: n }, (_, i) => ({ inMs: start + i * (lenMs + gap), outMs: start + i * (lenMs + gap) + lenMs }));
}
/**
 * How many cuts each moment gives so a remix with few moments reaches its length at its pace (Dax,
 * 2026-10-06): three moments of a 5 to 7 s TikTok edit become six or nine fast cuts, never three slow ones.
 * Sources come best first; each round gives every moment that can hold one more cut another, until the cut
 * reaches `minMs`, never past `maxMs` or REMIX_CUTS_PER_MOMENT.
 */
export function splitCounts(sources, totalMs, minMs, maxMs) {
    const counts = sources.map(() => 1);
    let total = totalMs;
    const gapOf = (len) => Math.round(len * REMIX_SPLIT_GAP_SHARE);
    for (let n = 2; n <= REMIX_CUTS_PER_MOMENT; n += 1) {
        for (const [i, src] of sources.entries()) {
            if (total >= minMs)
                return counts;
            if (!src.split || counts[i] !== n - 1 || total + src.lenMs > maxMs)
                continue;
            if (n * src.lenMs + (n - 1) * gapOf(src.lenMs) > src.spanMs)
                continue;
            counts[i] = n;
            total += src.lenMs;
        }
    }
    return counts;
}
/** The pace a platform scales when the brand has set none: Instagram's own, 700 to 1,400 ms a cut. */
export const REMIX_BASE_PACE = { minMs: 700, maxMs: 1400 };
/** Trending tracks are short cuts of copyrighted songs: fit only for cuts this long or shorter. */
export const TRENDING_MAX_CUT_MS = 20_000;
/**
 * The moments a remix keeps, best first until the cut reaches its length (Dax, 2026-10-01): each takes
 * its whole spoken lines where they still fit, else its paced cut (which mutes what it chops), and a
 * moment that fits neither way is skipped for a shorter one. Without a length every moment goes in,
 * whole. The result plays in the clip's order.
 */
export function chooseRemixCuts(candidates, minMs, maxMs) {
    if (minMs === null || maxMs === null) {
        return [...candidates].sort((a, b) => a.order - b.order).map((c) => ({ id: c.id, whole: c.wholeMs !== null }));
    }
    const best = [...candidates].sort((a, b) => a.rank - b.rank || b.confidence - a.confidence || a.order - b.order);
    const chosen = [];
    let total = 0;
    for (const c of best) {
        if (total >= minMs)
            break;
        if (c.wholeMs !== null && total + c.wholeMs <= maxMs) {
            chosen.push({ c, whole: true });
            total += c.wholeMs;
        }
        else if (total + c.pacedMs <= maxMs) {
            chosen.push({ c, whole: false });
            total += c.pacedMs;
        }
    }
    return chosen.sort((a, b) => a.c.order - b.c.order).map(({ c, whole }) => ({ id: c.id, whole }));
}
//# sourceMappingURL=compose.js.map